import styled, { css } from 'styled-components';

import {
  grey1,
  grey2,
  grey3,
  grey4,
  grey5,
  grey6,
  grey10,
  coolGrey05,
} from 'approot/shared/styles/colours.style';
import { mediaUp, rem } from 'approot/shared/style';
import {
  spacing4,
  spacing8,
  spacing16,
  spacing40,
} from 'approot/shared/styles/spacing.style';
import {
  baseBoxShadowFocus,
  borderRadius16,
  standardFocusState,
} from 'approot/shared/styles/borders.style';
import { Heading16, fontSize24 } from 'approot/shared/styles/typography.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  dropdownItemCommonCss,
  DropdownItemProps,
} from 'approot/shared/dropdown/dropdown-item.style';

const MEDIA_MORE_THAN_360 = '@media (min-width: 360px)';

const activeStateCss = css`
  &:active {
    cursor: pointer;
    color: ${grey10};
    border: 1px solid ${grey4};
    background-color: ${grey4};

    svg {
      stroke: ${grey10};
    }
  }
`;

const commonStates = css`
  ${activeStateCss}

  &:hover {
    cursor: pointer;
    border: 1px solid ${grey5};
    background-color: ${grey2};
  }

  &:disabled,
  &:disabled:hover {
    cursor: not-allowed;
    color: ${grey6};
    border: 1px solid ${coolGrey05};
    background-color: ${coolGrey05};

    svg {
      stroke: ${grey6};
    }
  }
`;

export const RichtextToolbar = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 ${rem(spacing16)} 0 ${rem(spacing8)};
  background-color: ${grey1};
  border-bottom: 1px solid ${grey3};
  ${mediaUp.md`
    padding: 0 ${rem(spacing40)};
  `}
`;

type RichtextToolbarRowProps = {
  $noBorder?: boolean;
};

export const RichtextToolbarRow = styled.div<RichtextToolbarRowProps>`
  display: flex;
  width: 100%;

  ${mediaUp.md`
    width: auto;
  `}
`;

export const RichtextDivider = styled.div`
  background-color: ${grey3};
  width: ${rem(1)};
  height: ${rem(40)};
  margin: ${rem(spacing4)} 0;
`;

type RichtextDropdownItemProps = {
  $fontSize?: number;
  $fontWeight?: number;
  $fontFamily?: string;
  $lineHeight?: number;
  $colour?: string;
} & DropdownItemProps;

export const RichtextDropdownItem = styled.button<RichtextDropdownItemProps>`
  ${dropdownItemCommonCss}

  display: flex;
  align-items: center;

  font-size: ${({ $fontSize = fontSize24 }) => rem($fontSize)};
  line-height: ${({ $lineHeight = 40 }) => rem($lineHeight)};

  ${({ $fontFamily }) =>
    $fontFamily &&
    `
    font-family: ${$fontFamily};
  `};
`;

type RichtextDropdownProps = {
  $padding?: number;
  $isRow?: boolean;
};

export const RichtextDropdown = styled.div<RichtextDropdownProps>`
  display: flex;
  flex-direction: ${({ $isRow }) => ($isRow ? 'row' : 'column')};
  background-color: ${grey1};
  border-radius: ${rem(borderRadius16)};
  border: 1px solid ${grey4};
  padding: ${({ $padding = spacing8 }) => rem($padding)};
  max-width: ${rem(320)};
  z-index: 1000;

  ${RichtextDropdownItem} {
    margin-bottom: ${rem(2)};
  }

  ${RichtextDropdownItem}:last-of-type {
    margin-bottom: 0;
  }
`;

type RichtextColourDotProps = {
  $colour: string;
};

export const RichtextColourDot = styled.span<RichtextColourDotProps>`
  border-radius: 100%;
  background-color: ${props => props.$colour};
  cursor: pointer;
  height: ${rem(20)};
  width: ${rem(20)};
  border: 2px solid ${grey1};
  pointer-events: none;
`;

export const RichtextDropdownIcon = styled.div`
  display: none;

  ${MEDIA_MORE_THAN_360} {
    display: block;
    margin-left: ${rem(spacing8)};
  }

  ${mediaUp.lg`
    margin-left: ${rem(spacing16)};
  `}
`;

export const RichtextDropdownButton = styled.button`
  ${commonStates}

  /* we can't use standardFocusState here because calling focus() in js doesn't set focus-visible */
  /* see handleKeydown in richtext.tsx */
  &:focus {
    box-shadow: ${baseBoxShadowFocus};
    outline: 0;
  }

  display: flex;
  align-items: center;
  background-color: ${grey1};
  border: 1px solid ${grey1};
  cursor: pointer;
  border-radius: ${rem(72)};
  padding: ${rem(spacing4)} ${rem(spacing8)};
  margin: 0 ${rem(spacing8)};
  color: ${grey10};
  height: ${rem(32)};
  white-space: nowrap;
  overflow: hidden;

  svg {
    stroke: ${grey10};
  }

  ${RichtextDropdownIcon} {
    stroke: ${grey10};
    fill: ${grey10};
  }

  ${mediaUp.md`
    margin: ${rem(spacing8)};
  `}

  &:active {
    ${RichtextDropdownIcon} {
      stroke: ${grey10};
      fill: ${grey10};
    }
  }
`;

export const RichtextDropdownItemTick = styled.span`
  width: ${rem(10)};
  margin-right: ${rem(spacing8)};

  svg {
    stroke: ${grey10};
  }
`;

type RichtextIconButtonProps = {
  $selected?: boolean;
};

const iconButtonCss = css<RichtextIconButtonProps>`
  ${standardFocusState}
  ${commonStates}

  display: flex;
  justify-content: center;
  align-items: center;

  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 100%;
  background-color: ${coolGrey05};
  border: 1px solid ${coolGrey05};
  margin: 0 ${rem(1)};

  svg {
    flex: 1 0 auto;
  }

  ${({ $selected }) =>
    $selected &&
    `  
      color: ${grey10};
      border: 1px solid ${grey4};
      background-color: ${grey4};

      svg {
        stroke: ${grey10};
      }

      &:hover {
        color: ${grey10};
        border: 1px solid ${grey4};
        background-color: ${grey4};
      }

      ${activeStateCss}
  `}
`;

export const RichtextIconButton = styled.button.attrs<RichtextIconButtonProps>(
  ({ $selected }) =>
    $selected !== undefined
      ? {
          role: 'checkbox',
          'aria-checked': $selected,
        }
      : {}
)`
  ${iconButtonCss}
`;

export const RichtextIconButtonXsHidden = styled.button<RichtextIconButtonProps>`
  ${iconButtonCss}

  display: none;

  ${MEDIA_MORE_THAN_360} {
    display: flex;
  }
`;

export const RichtextIconGroup = styled.div`
  display: flex;
  margin: ${rem(spacing8)};
`;

type RichtextToolbarWrapperProps = {
  $show?: boolean;
};

export const RichtextToolbarWrapper = styled.div<RichtextToolbarWrapperProps>`
  width: 100%;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;

  ${({ $show }) =>
    $show &&
    `    
      max-height: 113px;
  `}
`;

type RichtextSmallWrapperProps = {
  $hide?: boolean;
};

export const RichtextSmallWrapper = styled.div<RichtextSmallWrapperProps>`
  display: ${({ $hide }) => ($hide ? 'none' : 'flex')};
  align-items: center;
`;

type RichtextArrowButtonProps = {
  $pointRight?: boolean;
};

export const RichtextArrowButton = styled.button<RichtextArrowButtonProps>`
  ${standardFocusState}
  ${commonStates}

  display: flex;
  justify-content: center;
  align-items: center;

  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 100%;
  background-color: ${coolGrey05};
  border: 1px solid ${coolGrey05};
  margin: 0 ${rem(spacing8)};

  svg {
    stroke: ${grey10};
    flex: 1 0 auto;
    ${({ $pointRight }) => $pointRight && `transform: rotate(180deg);`}
  }
`;

export const RichtextColourPanel = styled(Box).attrs({
  $flexWrap: 'wrap',
  $justifyContent: 'space-evenly',
})`
  max-width: ${rem(180)};
  margin-bottom: ${({ $marginBottom = 0 }) => rem($marginBottom)};
`;

export const RichtextColourPanelHeading = styled(Heading16)`
  margin-bottom: ${rem(spacing8)};
  margin-left: ${rem(spacing8)};
`;
